/**
 * Buy & Hold solution page.
 */
import { Home, Landmark, Percent, Users2, LineChart, LayoutDashboard } from "lucide-react";
import SolutionPageTemplate from "../SolutionPageTemplate";

export default function BuyAndHoldSolutionPage() {
  return (
    <SolutionPageTemplate
      slug="buy-and-hold"
      persona="rental portfolio operators"
      eyebrow="Solutions · Buy &amp; Hold"
      title="Underwrite for cash flow."
      titleAccent="Hold like a fund."
      subtitle="Every rental scored on DSCR, cash-on-cash, and stabilized cap rate — before you buy. Every held asset re-scored nightly against live market rents — after you own it."
      bullets={[
        "Pre-purchase DSCR and cash-on-cash modelling with sensitivity bands",
        "Post-purchase cash-flow health re-scored nightly (BP-021 · BP-023)",
        "Loan-maturity and lease-expiry radar with proactive alerts",
      ]}
      metricStrip={[
        { stat: "8.4%", label: "avg. cap rate held" },
        { stat: "94%", label: "occupancy across portfolio" },
        { stat: "1.32", label: "avg. DSCR" },
        { stat: "$412k", label: "equity unlocked via refi" },
      ]}
      pains={[
        { title: "Optimistic rent assumptions", desc: "You underwrote at $1,850 rent. Local market clears at $1,650. Cash flow disappears." },
        { title: "Stale portfolio data", desc: "You don't actually know current cap rate or DSCR across all your properties — until tax time." },
        { title: "Missed refi windows", desc: "Two properties built enough equity to BRRRR — you noticed nine months too late." },
        { title: "Balloon surprise", desc: "A loan matured six weeks ago. You found out when the bank called." },
      ]}
      playbook={[
        { step: 1, title: "Underwrite", desc: "DSCR, CoC, cap rate — all modelled with local rent + expense data.", bp: "BP-021 · BP-014" },
        { step: 2, title: "Acquire", desc: "Full workspace with financing, insurance, tax, and lease docs.", bp: "BP-002" },
        { step: 3, title: "Manage", desc: "Nightly re-score. Occupancy, NOI, DSCR live. Watch bands fire alerts.", bp: "BP-023" },
        { step: 4, title: "Recycle", desc: "BRRRR refi opportunities surface automatically as equity builds.", bp: "BP-023 · BP-014" },
      ]}
      productsUsed={[
        { icon: Landmark, title: "Portfolio Intelligence", to: "/products/portfolio-intelligence", desc: "Cash-flow health, cap rate, occupancy, maturity radar for the entire portfolio." },
        { icon: Percent, title: "Strategy Comparison", to: "/products/strategy-comparison", desc: "Buy-and-Hold scored against BRRRR, Sub-To, and Seller-Finance on every property." },
        { icon: LineChart, title: "Market Intelligence", to: "/products/market-intelligence", desc: "Rent growth, cap rate direction, and forecast per ZIP — buy where signals compound." },
        { icon: Home, title: "Property Workspace", to: "/products/property-workspace", desc: "Leases, insurance, tax, tenant comms — one command center per asset." },
        { icon: Users2, title: "AI Acquisition Coach", to: "/products/ai-acquisition-coach", desc: "Buy-vs-hold-vs-refi recommendation cited with DSCR and cap rate evidence." },
        { icon: LayoutDashboard, title: "Executive Dashboard", to: "/products/executive-dashboard", desc: "Portfolio NOI, occupancy, DSCR aggregated across six dimensions." },
      ]}
      proof={{
        stat: "22",
        label: "doors managed on one platform",
        quote: "Portfolio Intelligence caught three refi-ready properties I hadn't noticed. Recycled $412k of equity in one quarter.",
        attribution: "Buy-and-hold operator · 22 doors",
      }}
      related={[
        { title: "BRRRR", to: "/solutions/brrrr", desc: "The refinancing loop that recycles capital automatically." },
        { title: "Creative Finance", to: "/solutions/creative-finance", desc: "Add Sub-To and Seller-Finance to your acquisition toolkit." },
        { title: "Fix & Flip", to: "/solutions/fix-and-flip", desc: "For rehabs that become rentals — same underwriting flow." },
      ]}
    />
  );
}
